import { useEffect, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import {
  listShopThreads,
  listShopVendorsPublicByIds,
  type ShopVendorPublic,
} from '@pc/db';
import { useToast } from '@pc/ui';
import { buyerThreadVendorLabel } from './inboxLabels';
import {
  SHOP_INBOX_NOTIFY_POLL_MS,
  shopInboxNotifyPollQueryOptions,
} from './inboxPolling';
import { notifyBrowser } from './browserNotifications';
import {
  detectNewShopMessageThreads,
  snapshotShopThreads,
  type ThreadSnap,
} from './shopMessageNotifications';

export interface ShopMessageNotificationWatcherProps {
  side: 'buyer' | 'vendor';
  buyerOrgId?: string;
  vendorId?: string;
  /** e.g. `/shop/inbox` or `/inbox` */
  inboxBasePath: string;
  enabled?: boolean;
  /**
   * When `true` (default) we only fire OS-level `Notification`s while the tab
   * is hidden — toasts already cover the foreground case and a paired toast
   * + system banner is noisy. Set `false` to always mirror toasts as system
   * notifications.
   */
  browserNotificationsOnlyWhenHidden?: boolean;
}

function useActiveInboxThreadId(inboxBasePath: string): string | null {
  const { pathname } = useLocation();
  const base = inboxBasePath.replace(/\/$/, '');
  const prefix = `${base}/`;
  if (!pathname.startsWith(prefix)) return null;
  const segment = pathname.slice(prefix.length).split('/')[0];
  return segment || null;
}

/**
 * Polls shop threads app-wide and surfaces incoming messages as both an
 * in-app toast and (when permitted + opted in) a browser-native
 * `Notification`. Suppresses delivery for the thread currently open in the
 * inbox.
 */
export function ShopMessageNotificationWatcher({
  side,
  buyerOrgId,
  vendorId,
  inboxBasePath,
  enabled = true,
  browserNotificationsOnlyWhenHidden = true,
}: ShopMessageNotificationWatcherProps) {
  const toast = useToast();
  const navigate = useNavigate();
  const activeThreadId = useActiveInboxThreadId(inboxBasePath);
  const prevRef = useRef<Map<string, ThreadSnap> | null>(null);
  const notifiedAtRef = useRef<Map<string, string>>(new Map());

  const ready =
    enabled && (side === 'buyer' ? Boolean(buyerOrgId) : Boolean(vendorId));

  const threadsQ = useQuery({
    queryKey: ['shop', 'message-notify', side, buyerOrgId ?? vendorId],
    queryFn: () =>
      listShopThreads({
        side,
        buyerOrgId,
        vendorId,
        pageSize: 50,
        countMode: 'none',
      }),
    enabled: ready,
    ...shopInboxNotifyPollQueryOptions,
    refetchInterval: ready ? SHOP_INBOX_NOTIFY_POLL_MS : false,
  });

  useEffect(() => {
    const threads = threadsQ.data?.rows;
    if (!threads) return;

    const { baseline, notifications } = detectNewShopMessageThreads(
      threads,
      prevRef.current,
      side,
      { activeThreadId },
    );

    prevRef.current = snapshotShopThreads(threads, side);

    if (baseline || notifications.length === 0) return;

    const fresh = notifications.filter(
      (n) => notifiedAtRef.current.get(n.threadId) !== n.lastMessageAt,
    );
    if (fresh.length === 0) return;

    void (async () => {
      let vendorById = new Map<string, ShopVendorPublic>();
      if (side === 'buyer') {
        const vendorIds = [...new Set(fresh.map((n) => n.vendorId))];
        const vendors = await listShopVendorsPublicByIds(vendorIds);
        vendorById = new Map(vendors.map((v) => [v.id, v]));
      }

      const base = inboxBasePath.replace(/\/$/, '');
      const tabHidden =
        typeof document !== 'undefined' && document.visibilityState === 'hidden';

      for (const n of fresh) {
        notifiedAtRef.current.set(n.threadId, n.lastMessageAt);
        const href = `${base}/${n.threadId}`;
        const otherLabel =
          side === 'buyer'
            ? buyerThreadVendorLabel(vendorById.get(n.vendorId))
            : 'a buyer';
        const text =
          side === 'buyer'
            ? `New message from ${otherLabel}`
            : 'New message from a buyer';

        toast.push(text, 'info', {
          onClick: () => navigate(href),
        });

        const shouldNotifyBrowser = browserNotificationsOnlyWhenHidden
          ? tabHidden
          : true;
        if (shouldNotifyBrowser) {
          notifyBrowser({
            tag: `shop-thread:${n.threadId}`,
            key: n.lastMessageAt,
            title:
              side === 'buyer'
                ? `New message — ${otherLabel}`
                : 'New buyer message',
            body:
              side === 'buyer'
                ? 'Open inbox to read and reply.'
                : 'Open inbox to read and reply.',
            onClick: () => navigate(href),
          });
        }
      }
    })();
  }, [
    threadsQ.data,
    threadsQ.dataUpdatedAt,
    side,
    activeThreadId,
    inboxBasePath,
    browserNotificationsOnlyWhenHidden,
    toast,
    navigate,
  ]);

  return null;
}
